import React from "react";
import { ECallProvider } from "@/common/enums";
import { TCallResponse } from "@/common/typings";
import TRTC from "trtc-sdk-v5";
import {
  RemoteTrack,
  Room,
  RoomEvent,
  Track,
  VideoTrack,
  LocalVideoTrack,
  LocalAudioTrack,
  RemoteParticipant,
  TrackPublication,
} from "livekit-client";
import { stringToArrayBuffer } from "@/common/utils";
import { CMD_ID_CALL, HANG_UP } from "@/common/cmd";

const LIVEKIT_TOPIC = "robot-control";

// 通用接口定义
export interface ICallAdapter {
  // 初始化
  initialize(): Promise<void>;

  // 进入房间
  enterRoom(params: any): Promise<void>;

  // 离开房间
  exitRoom(): Promise<void>;

  // 开始本地视频
  startLocalVideo(params: any): Promise<void>;

  // 停止本地视频
  stopLocalVideo(): Promise<void>;

  // 开始本地音频
  startLocalAudio(): Promise<void>;

  // 停止本地音频
  stopLocalAudio(): Promise<void>;

  // 开始远端视频
  startRemoteVideo(params: any): Promise<void>;

  // 发送消息
  sendMessage(cmdId: number, data: string): Promise<void>;

  // 切换摄像头
  switchCamera(
    useFrontCamera: boolean,
    view?: HTMLDivElement | HTMLMediaElement | null
  ): Promise<void>;

  // 设置事件监听器
  installEventListeners(callbacks: {
    onRemoteUser?: (user: any) => void;
    onHangup?: () => void;
    onRemoteVideoRef?: (
      ref: React.RefObject<HTMLDivElement | HTMLVideoElement | null>
    ) => void;
  }): void;

  // 移除事件监听器
  uninstallEventListeners(): void;

  // 获取适配器类型
  getProvider(): ECallProvider;

  // 获取SDK实例
  getSDKInstance(): any;

  // 渲染视频元素
  renderVideoElements(
    containerType: "remote" | "local",
    ref: React.RefObject<HTMLDivElement | HTMLVideoElement | null>,
    remoteFullRef?: React.RefObject<HTMLDivElement | HTMLVideoElement | null>,
    dragPosition?: { x: number; y: number },
    isDragging?: boolean,
    onDragStart?: (e: any) => void,
    onDragMove?: (e: any) => void,
    onDragEnd?: (e: any) => void,
    onChangeCamera?: () => void
  ): React.ReactElement | null;
}

// 工厂函数
export const createCallAdapter = (
  provider: ECallProvider,
  response: TCallResponse
): ICallAdapter => {
  switch (provider) {
    case ECallProvider.Trtc:
      return new TrtcAdapter(response);
    case ECallProvider.LiveKit:
      return new LiveKitAdapter(response);
    default:
      throw new Error(`Unsupported provider: ${provider}`);
  }
};

// TRTC 适配器实现
class TrtcAdapter implements ICallAdapter {
  private trtc: any;
  private response: TCallResponse;

  constructor(response: TCallResponse) {
    this.response = response;
  }

  async initialize(): Promise<void> {
    this.trtc = TRTC.create();
  }

  async enterRoom(params: {
    userId: string;
    userSig: string;
    appId: number;
    roomId: number;
  }): Promise<void> {
    await this.trtc?.enterRoom({
      sdkAppId: params.appId,
      userId: params.userId,
      userSig: params.userSig,
      roomId: params.roomId,
      enableAutoPlayDialog: false,
    });
  }

  async exitRoom(): Promise<void> {
    await this.stopLocalAudio();
    await this.stopLocalVideo();
    await this.trtc?.exitRoom();
    await this.trtc?.destroy();
  }

  async startLocalVideo(params: {
    view: HTMLElement;
    option?: any;
  }): Promise<void> {
    await this.trtc?.startLocalVideo(params);
  }

  async stopLocalVideo(): Promise<void> {
    await this.trtc?.stopLocalVideo();
  }

  async startLocalAudio(): Promise<void> {
    await this.trtc?.startLocalAudio();
  }

  async stopLocalAudio(): Promise<void> {
    await this.trtc?.stopLocalAudio();
  }

  async startRemoteVideo({
    userId,
    streamType,
    view,
    fullView,
  }: {
    userId: string;
    streamType: any;
    view: HTMLVideoElement;
    fullView: HTMLVideoElement;
  }): Promise<void> {
    await this.trtc?.startRemoteVideo({ userId, streamType });
    const videoTrack = this.trtc?.getVideoTrack({ userId, streamType });
    if (videoTrack) {
      view.srcObject = new MediaStream([videoTrack]);
      fullView.srcObject = new MediaStream([videoTrack]);
      view.play();
      fullView.play();
    }
  }

  async sendMessage(cmdId: number, data: string): Promise<void> {
    await this.trtc?.sendCustomMessage({
      cmdId,
      data: stringToArrayBuffer(data),
    });
  }

  async switchCamera(
    useFrontCamera: boolean,
    view?: HTMLMediaElement
  ): Promise<void> {
    await this.trtc?.updateLocalVideo({
      mute: false,
      option: { useFrontCamera, mirror: useFrontCamera },
    });
  }

  installEventListeners(callbacks: {
    onRemoteUser?: (user: any) => void;
    onHangup?: () => void;
    onRemoteVideoRef?: (
      ref: React.RefObject<HTMLDivElement | HTMLVideoElement | null>
    ) => void;
  }): void {
    let currentRemoteUser: any = null;

    const handleVideoAvailable = async ({
      userId,
      streamType,
    }: {
      userId: string;
      streamType: any;
    }) => {
      currentRemoteUser = {
        userId,
        streamType,
        videoAvailable: true,
      };
      if (callbacks.onRemoteUser) {
        callbacks.onRemoteUser(currentRemoteUser);
      }
    };

    const handleVideoUnavailable = (event: any) => {
      event.result.forEach(({ userId }: { userId: string }) => {
        if (currentRemoteUser?.userId === userId) {
          currentRemoteUser = null;
          if (callbacks.onRemoteUser) {
            callbacks.onRemoteUser(undefined);
          }
        }
      });
    };

    const handleCustomMessage = (event: any) => {
      const { cmdId, data } = event;
      if (cmdId === CMD_ID_CALL) {
        const message = new TextDecoder().decode(data);
        if (message === HANG_UP) {
          if (callbacks.onHangup) {
            callbacks.onHangup();
          }
        }
      }
    };

    const handleAutoPlayFailed = (event: any) => {
      console.log("handleAutoPlayFailed", event);
      const { resume } = event;
      resume();
    };

    this.trtc?.on(TRTC.EVENT.REMOTE_VIDEO_AVAILABLE, handleVideoAvailable);
    this.trtc?.on(TRTC.EVENT.REMOTE_VIDEO_UNAVAILABLE, handleVideoUnavailable);
    this.trtc?.on(TRTC.EVENT.CUSTOM_MESSAGE, handleCustomMessage);
    this.trtc?.on(TRTC.EVENT.AUTOPLAY_FAILED, handleAutoPlayFailed);
  }

  uninstallEventListeners(): void {
    // TRTC 事件监听移除在具体实现中处理
  }

  getProvider(): ECallProvider {
    return ECallProvider.Trtc;
  }

  getSDKInstance(): any {
    return this.trtc;
  }

  renderVideoElements(
    containerType: "remote" | "local",
    ref: React.RefObject<HTMLDivElement | HTMLVideoElement | null>,
    remoteFullRef?: React.RefObject<HTMLVideoElement | null>,
    dragPosition?: { x: number; y: number },
    isDragging?: boolean,
    onDragStart?: (e: any) => void,
    onDragMove?: (e: any) => void,
    onDragEnd?: (e: any) => void,
    onChangeCamera?: () => void
  ): React.ReactElement | null {
    if (containerType === "remote") {
      return (
        <div className="relative top-0 left-0 w-full h-full">
          <video
            ref={ref as any}
            className="absolute top-0 left-0 w-full h-full object-cover"
          />
          <video
            ref={remoteFullRef as any}
            className="absolute top-0 left-0 w-full h-36 object-cover"
          />
        </div>
      );
    } else {
      // 为 local 视频添加拖拽容器
      return (
        <div
          className={`absolute landscape:w-48 landscape:h-32 w-32 h-20 aspect-square bg-black rounded overflow-hidden`}
          style={{
            transform: `translate(${dragPosition?.x || 0}px, ${
              dragPosition?.y || 0
            }px)`,
            transition: isDragging ? "none" : "transform 0.3s ease",
            cursor: "move",
            userSelect: "none",
          }}
          onMouseDown={onDragStart}
          onMouseMove={onDragMove}
          onMouseUp={onDragEnd}
          onMouseLeave={onDragEnd}
          onTouchStart={onDragStart}
          onTouchMove={onDragMove}
          onTouchEnd={onDragEnd}
          onClick={onChangeCamera}
        >
          <div ref={ref as any} className="w-full h-full object-cover" />
        </div>
      );
    }
  }
}

// LiveKit 适配器实现
class LiveKitAdapter implements ICallAdapter {
  private room?: Room;
  private audioTrack: Track | undefined;
  private localAudioTrack: LocalAudioTrack | undefined;
  private cameras: MediaDeviceInfo[] = [];
  private callbacks?: {
    onRemoteUser?: (user: any) => void;
    onHangup?: () => void;
    onRemoteVideoRef?: (
      ref: React.RefObject<HTMLDivElement | HTMLVideoElement | null>
    ) => void;
  };

  constructor(response: TCallResponse) {}

  async initialize(): Promise<void> {
    this.room = new Room({
      adaptiveStream: true,
      dynacast: true,
      videoCaptureDefaults: {
        facingMode: "user",
      },
    });
    await this.getCameras();
  }

  async getCameras() {
    try {
      const devices = await navigator.mediaDevices.enumerateDevices();
      const videoDevices = devices.filter(
        (device) => device.kind === "videoinput"
      );
      this.cameras = videoDevices;
    } catch (err) {
      console.error("Failed to enumerate devices", err);
    }
  }

  async enterRoom(params: { url: string; token: string }): Promise<void> {
    await this.room?.connect(params.url, params.token, {
      autoSubscribe: true,
    });
  }

  async exitRoom(): Promise<void> {
    await this.stopLocalAudio();
    await this.stopLocalVideo();
    await this.room?.disconnect();
  }

  async startLocalVideo(params: { view: HTMLVideoElement }): Promise<void> {
    await this.room?.localParticipant.setCameraEnabled(true);
    const videoPublication = this.room?.localParticipant.getTrackPublication(
      Track.Source.Camera
    );
    if (videoPublication?.track && params.view) {
      await videoPublication.track.attach(params.view);
    }
  }

  async stopLocalVideo(): Promise<void> {
    await this.room?.localParticipant.setCameraEnabled(false);
  }

  async startLocalAudio(): Promise<void> {
    this.audioTrack?.attach();
    await this.room?.localParticipant.setMicrophoneEnabled(true);
    // 获取并存储本地音频 Track
    const audioPublication = this.room?.localParticipant.getTrackPublication(
      Track.Source.Microphone
    );
    if (audioPublication?.track) {
      this.localAudioTrack = audioPublication.track as LocalAudioTrack;
    }
  }

  async stopLocalAudio(): Promise<void> {
    // 停止本地音频 Track
    if (this.localAudioTrack) {
      this.localAudioTrack.stop();
      this.localAudioTrack = undefined;
    }
    await this.room?.localParticipant.setMicrophoneEnabled(false);
  }

  async startRemoteVideo({
    track,
    view,
  }: {
    track: RemoteTrack<Track.Kind>;
    view: HTMLVideoElement;
  }): Promise<void> {
    if (view) {
      if (track.kind === "video") {
        const videoTrack = track as VideoTrack;
        videoTrack.attach(view);
      } else if (track.kind === "audio") {
        track.attach(view);
      }
    } else if (track.kind === "audio") {
      this.audioTrack = track;
      if(this.room?.localParticipant.isMicrophoneEnabled) {
        this.audioTrack.attach();
      }
    }
  }

  async sendMessage(cmdId: number, data: string): Promise<void> {
    const message = JSON.stringify({ cmdId, data });
    try {
      await this.room?.localParticipant.sendText(message, {
        topic: LIVEKIT_TOPIC,
      });
    } catch (error) {
      console.error("[CallAdapter] sendMessage failed:", error);
    }
  }

  async switchCamera(
    useFrontCamera: boolean,
    view?: HTMLMediaElement
  ): Promise<void> {
    try {
      // 定义摄像头约束
      const facingMode = useFrontCamera ? "user" : "environment";
      // 查找匹配的摄像头设备
      let targetDevice = this.cameras.find(
        (cam) =>
          (facingMode === "user" &&
            (cam.label.toLowerCase().includes("front") ||
              cam.label.includes("user"))) ||
          (facingMode === "environment" &&
            (cam.label.toLowerCase().includes("back") ||
              cam.label.includes("environment")))
      );

      if (!targetDevice) {
        // 重新启用摄像头时传递约束
        const constraints: MediaTrackConstraints = { facingMode };

        // 尝试通过创建新的媒体流来切换摄像头
        const newStream = await navigator.mediaDevices.getUserMedia({
          video: constraints,
        });
        const newVideoTrack = newStream.getVideoTracks()[0];
        targetDevice = {
          deviceId: newVideoTrack.getSettings().deviceId || "",
          kind: "videoinput",
          label: `Camera (${facingMode})`,
          groupId: "",
          toJSON: () => ({}),
        };
        // 停掉临时流
        newVideoTrack.stop();
      }

      // 替换本地视频 track
      const localParticipant = this.room?.localParticipant;
      // 取消发布当前摄像头
      let currentCamTrackPublication = localParticipant?.getTrackPublication(
        Track.Source.Camera
      );
      if (currentCamTrackPublication?.track) {
        currentCamTrackPublication.track.stop();
        await localParticipant?.unpublishTrack(
          currentCamTrackPublication.track
        );
      }

      // 请求新视频流
      const stream = await navigator.mediaDevices.getUserMedia({
        video: { deviceId: { exact: targetDevice.deviceId } },
      });

      // 发布新 track
      const localVideoTrack = new LocalVideoTrack(
        stream.getVideoTracks()[0],
        undefined,
        true
      );
      await localParticipant?.publishTrack(localVideoTrack, {
        source: Track.Source.Camera,
      });

      currentCamTrackPublication = localParticipant?.getTrackPublication(
        Track.Source.Camera
      );
      await currentCamTrackPublication?.track?.attach(view!);
      // await localParticipant.setCameraEnabled(true);
      console.log(
        `[LiveKit] switched to ${useFrontCamera ? "front" : "back"} camera`
      );
    } catch (error) {
      console.error("[LiveKit] switchCamera failed:", error);
      // 如果出错，回退到简单的重新启用
      await this.room?.localParticipant.setCameraEnabled(true);
      throw error;
    }
  }

  handleTrackSubscribled = (
    track: Track,
    _publication: TrackPublication,
    participant: RemoteParticipant
  ) => {
    console.log("TrackSubscribed", track, participant, _publication);
    this.callbacks?.onRemoteUser?.({
      track,
      ...participant,
      videoAvailable: true,
    });
  };

  handleTrackUnsubscribled = (
    track: any,
    _publication: any,
    _participant: any
  ) => {
    console.log("TrackUnsubscribed", track);
    if (track.kind === "video") {
      const videoTrack = track as VideoTrack;
      videoTrack.detach();
      this.callbacks?.onRemoteUser?.(undefined);
    }
  };

  handleParticipantDisconnected = () => {
    console.log("Room disconnected");
    if (this.room?.remoteParticipants.size === 0) {
      this.callbacks?.onHangup?.();
    }
  }

  installEventListeners(callbacks: {
    onRemoteUser?: (user: any) => void;
    onHangup?: () => void;
    onRemoteVideoRef?: (
      ref: React.RefObject<HTMLDivElement | HTMLVideoElement | null>
    ) => void;
  }): void {
    this.callbacks = callbacks;
    this.room?.on(
      RoomEvent.TrackSubscribed,
      this.handleTrackSubscribled.bind(this)
    );

    this.room?.on(RoomEvent.TrackUnsubscribed, this.handleTrackUnsubscribled.bind(this));
    this.room?.on(
      RoomEvent.ParticipantDisconnected,
      this.handleParticipantDisconnected.bind(this)
    );

    this.room?.registerTextStreamHandler(
      LIVEKIT_TOPIC,
      async (reader, participantInfo) => {
        console.log("Received text message from =========>", participantInfo, reader);
        const message = await reader.readAll();
        try {
          const parsed = JSON.parse(message);
          const { cmdId, data } = parsed;
          if (cmdId === CMD_ID_CALL) {
            if (data === HANG_UP) {
              callbacks?.onHangup?.();
            }
          }
        } catch (error) {
          console.error("Failed to parse data message:", error);
        }
      }
    );
  }

  uninstallEventListeners(): void {
    // LiveKit 事件监听移除在具体实现中处理
    this.room?.off(RoomEvent.TrackSubscribed, this.handleTrackSubscribled);
    this.room?.off(RoomEvent.TrackUnsubscribed, this.handleTrackUnsubscribled);
    this.room?.unregisterTextStreamHandler(LIVEKIT_TOPIC);
  }

  getProvider(): ECallProvider {
    return ECallProvider.LiveKit;
  }

  getSDKInstance(): any {
    return this.room;
  }

  renderVideoElements(
    containerType: "remote" | "local",
    ref: React.RefObject<HTMLDivElement | HTMLVideoElement | null>,
    remoteFullRef?: React.RefObject<HTMLDivElement | HTMLVideoElement | null>,
    dragPosition?: { x: number; y: number },
    isDragging?: boolean,
    onDragStart?: (e: any) => void,
    onDragMove?: (e: any) => void,
    onDragEnd?: (e: any) => void,
    onChangeCamera?: () => void
  ): React.ReactElement | null {
    if (containerType === "remote") {
      return (
        <video
          ref={ref as any}
          className="absolute top-0 left-0 w-full h-full object-cover"
          autoPlay={true}
          playsInline={true}
          muted={false}
        />
      );
    } else {
      // 为 local 视频添加拖拽容器
      return (
        <div
          className={`absolute landscape:w-48 landscape:h-32 w-32 h-20 aspect-square bg-black rounded overflow-hidden`}
          style={{
            transform: `translate(${dragPosition?.x || 0}px, ${
              dragPosition?.y || 0
            }px)`,
            transition: isDragging ? "none" : "transform 0.3s ease",
            cursor: "move",
            userSelect: "none",
          }}
          onMouseDown={onDragStart}
          onMouseMove={onDragMove}
          onMouseUp={onDragEnd}
          onMouseLeave={onDragEnd}
          onTouchStart={onDragStart}
          onTouchMove={onDragMove}
          onTouchEnd={onDragEnd}
          onClick={onChangeCamera}
        >
          <video
            ref={ref as any}
            className="w-full h-full object-cover"
            style={{ transform: "scaleX(-1)" }}
            autoPlay={true}
            playsInline={true}
            muted={false}
          />
        </div>
      );
    }
  }
}
